/**
 * URL查询参数同步工具函数
 * 用于在列表页面中同步筛选条件和分页状态到URL
 */

import { FilterChangeParams } from '@/components/Filter';
import { PaginationConfig } from '@/components/DataTable';

export interface UrlSyncParams {
  filters?: FilterChangeParams;
  pagination?: {
    current?: number;
    pageSize?: number;
  };
}

/**
 * 从URL查询参数中解析筛选和分页状态
 */
export function parseUrlParams(searchParams: URLSearchParams): UrlSyncParams {
  const result: UrlSyncParams = {
    filters: {},
    pagination: {},
  };

  // 解析筛选参数
  for (const [key, value] of searchParams.entries()) {
    if (key === 'page') {
      result.pagination!.current = parseInt(value) || 1;
    } else if (key === 'pageSize') {
      result.pagination!.pageSize = parseInt(value) || 10;
    } else if (key === 'dateRange') {
      // 处理日期范围参数
      try {
        const dateRange = JSON.parse(decodeURIComponent(value));
        if (Array.isArray(dateRange) && dateRange.length === 2) {
          result.filters![key] = dateRange as [string, string];
        }
      } catch (e) {
        // 忽略解析错误
      }
    } else {
      // 其他筛选参数
      if (value) {
        // 尝试解析数字
        const numValue = parseInt(value);
        if (!isNaN(numValue) && numValue.toString() === value) {
          result.filters![key] = numValue;
        } else {
          result.filters![key] = value;
        }
      }
    }
  }

  return result;
}

/**
 * 将筛选和分页状态同步到URL查询参数
 */
export function syncParamsToUrl(params: UrlSyncParams, navigate: (path: string) => void, currentPath: string): void {
  const searchParams = new URLSearchParams();

  // 添加筛选参数
  if (params.filters) {
    Object.entries(params.filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (key === 'dateRange' && Array.isArray(value)) {
          // 特殊处理日期范围
          searchParams.set(key, encodeURIComponent(JSON.stringify(value)));
        } else {
          searchParams.set(key, value.toString());
        }
      }
    });
  }

  // 添加分页参数
  if (params.pagination) {
    if (params.pagination.current && params.pagination.current > 1) {
      searchParams.set('page', params.pagination.current.toString());
    }
    if (params.pagination.pageSize && params.pagination.pageSize > 10) {
      searchParams.set('pageSize', params.pagination.pageSize.toString());
    }
  }

  // 构建新的URL
  const queryString = searchParams.toString();
  const newPath = queryString ? `${currentPath}?${queryString}` : currentPath;

  // 使用replace避免在浏览器历史中创建过多条目
  navigate(newPath);
}

/**
 * 从URL参数初始化筛选条件
 * 合并默认值和URL参数
 */
export function initializeFiltersFromUrl(urlParams: URLSearchParams, defaultFilters: FilterChangeParams): FilterChangeParams {
  const urls = parseUrlParams(urlParams);

  return {
    ...defaultFilters,
    ...urls.filters,
  };
}

/**
 * 从URL参数初始化分页配置
 */
export function initializePaginationFromUrl(urlParams: URLSearchParams, defaultPagination: PaginationConfig): PaginationConfig {
  const urls = parseUrlParams(urlParams);
  return {
    ...defaultPagination,
    current: urls.pagination?.current || defaultPagination.current || 1,
    pageSize: urls.pagination?.pageSize || defaultPagination.pageSize || 10,
  };
}

/**
 * 清理空值参数
 */
export function cleanEmptyParams(params: Record<string, any>): Record<string, any> {
  const cleaned: Record<string, any> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value) && value.length === 0) {
        // 跳过空数组
        return;
      }
      cleaned[key] = value;
    }
  });

  return cleaned;
}
